import { useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";

type ManifestType = "customer" | "internal";

const MANIFEST_PATHS: Record<ManifestType, string> = {
  customer: "/manifest-customer.json",
  internal: "/manifest-internal.json",
};

const THEME_COLORS: Record<ManifestType, string> = {
  customer: "#000000",
  internal: "#5B21B6",
};

/**
 * Dynamically switches the PWA manifest based on user role.
 * - Owners and Tenants get the black-themed customer manifest
 * - Employees and Service Providers get the purple-themed internal manifest
 * - Default (not logged in) uses the customer manifest
 */
export function useManifestSwitcher() {
  const { roles, isLoading } = useAuth();

  useEffect(() => {
    // Don't switch manifest while auth is loading
    if (isLoading) return;

    // Internal users: employees get the purple-themed manifest
    // Customer users (owners, tenants) and admins get the black-themed manifest
    const hasEmployeeRole = roles.some((r) => r.role === "employee");
    
    const manifestType: ManifestType = hasEmployeeRole ? "internal" : "customer";

    // Update manifest link in DOM
    const manifestLink = document.getElementById("manifest-link");
    if (manifestLink instanceof HTMLLinkElement) {
      manifestLink.href = MANIFEST_PATHS[manifestType];
    }

    // Update theme-color meta tag to match
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta instanceof HTMLMetaElement) {
      themeColorMeta.content = THEME_COLORS[manifestType];
    }
  }, [roles, isLoading]);
}
